import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGS,
  DEFAULT_LANG,
  isLanguageCode,
} from '../i18n/languages';
import { trackPageView } from '../utils/analytics';

const ORIGIN = 'https://passport.aranortravel.com';
const LOGO_URL = `${ORIGIN}/images/logo.png`;

// Path without the leading /:lang segment (e.g. "/locations"). "" = home.
function stripLang(pathname: string): string {
  const parts = pathname.split('/');
  if (isLanguageCode(parts[1])) {
    return '/' + parts.slice(2).join('/').replace(/\/$/, '');
  }
  return pathname.replace(/\/$/, '');
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Idempotent Organization JSON-LD — gives Google Search the logo to
// render alongside listings. Emitted once on the home page only; a
// site-wide block would duplicate the entity on every URL.
function upsertOrganizationJsonLd(): void {
  let el = document.head.querySelector<HTMLScriptElement>(
    'script[type="application/ld+json"][data-seo="organization"]',
  );
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Aranor Passport',
    url: ORIGIN,
    logo: LOGO_URL,
  };
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.seo = 'organization';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

type Props = {
  titleKey?: string;
  descriptionKey?: string;
  // Raw values (e.g. a localized venue name) take precedence over keys.
  title?: string;
  description?: string;
  // Absolute URL for og:image (social card). Pages that have a hero
  // image should pass it; otherwise the og:image meta is omitted.
  image?: string | null;
};

// Per-page <head>: localized title/description, canonical, and a full
// hreflang alternate set. Mutates document.head so it works at runtime
// and is captured verbatim by the build-time prerenderer.
const Seo: React.FC<Props> = ({
  titleKey = 'seo.default_title',
  descriptionKey = 'seo.default_description',
  title: rawTitle,
  description: rawDescription,
  image,
}) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = isLanguageCode(i18n.language) ? i18n.language : DEFAULT_LANG;

  const basePath = stripLang(pathname);
  const canonical = `${ORIGIN}/${lang}${basePath}`;
  const title = rawTitle ?? t(titleKey);
  const description = rawDescription ?? t(descriptionKey);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = title;
    trackPageView(pathname, title);
    upsertMeta('name', 'description', description);
    upsertLink('canonical', canonical);
    for (const code of SUPPORTED_LANGS) {
      upsertLink('alternate', `${ORIGIN}/${code}${basePath}`, code);
    }
    upsertLink(
      'alternate',
      `${ORIGIN}/${DEFAULT_LANG}${basePath}`,
      'x-default',
    );
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');
    // Falls back to the brand logo so social cards always have an
    // image, even on pages that don't pass a hero (privacy, terms,
    // my-stamps logged-out, etc.).
    upsertMeta('property', 'og:image', image ?? LOGO_URL);
    // Org markup only on the home page — one canonical Organization
    // entity per site, not one per URL. stripLang returns '/' for the
    // home and '' would only occur if the prefix branch wasn't taken;
    // accept both so we don't depend on that quirk.
    if (basePath === '' || basePath === '/') upsertOrganizationJsonLd();
  }, [lang, title, description, canonical, basePath, image, pathname]);

  return null;
};

export default Seo;
